import type { ReactNode } from 'react';

import { useState, useEffect, useContext, createContext } from 'react';

import { formatDocumentTitle } from 'src/utils/documentTitle';

const TitleClaim = createContext<(title: string | null) => void>(() => undefined);

/**
 * Where the title a screen claims with `useDocumentTitle` becomes the document's `<title>`. One
 * host stands around every screen (`root.tsx`), so a claimed title is an element React itself puts
 * into `<head>` — the same way the server-rendered screens name the tab (`DocumentHead`) — and the
 * two never leave a second, stale `<title>` behind for the browser to pick.
 */
export const DocumentTitleHost = ({ children }: { children: ReactNode }) => {
  const [claimed, setClaimed] = useState<string | null>(null);

  return (
    <TitleClaim.Provider value={setClaimed}>
      {claimed ? <title>{claimed}</title> : null}
      {children}
    </TitleClaim.Provider>
  );
};

/**
 * Names the browser tab after the screen that is open (BUG-15).
 *
 * A screen calls it once with its parts; the title follows them as they change and is given up when
 * the screen unmounts, so a route that forgot to claim the title does not inherit the previous
 * screen's. For the screens a session opens — the browser's to render. A page the server renders
 * says its title, with the rest of what a search engine reads, through `DocumentHead` instead.
 */
export const useDocumentTitle = (...parts: (string | null | undefined)[]): void => {
  const claim = useContext(TitleClaim);
  const title = formatDocumentTitle(...parts);

  useEffect(() => {
    claim(title);

    return () => claim(null);
  }, [claim, title]);
};
